import { getFloatingLayerZIndex } from "./floating-layer";
import { useMotionPresence } from "@/hooks/useMotionPresence";
import * as React from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  Placement,
  FloatingPortal,
} from "@floating-ui/react";

import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

// Context para TooltipProvider (delay global)
interface TooltipProviderContextValue {
  delayDuration: number;
}

const TooltipProviderContext = React.createContext<TooltipProviderContextValue>({
  delayDuration: 400,
});

// Context para compartilhar estado entre componentes do Tooltip
interface TooltipContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  floatingRef: React.MutableRefObject<HTMLDivElement | null>;
  getReferenceProps: (props?: any) => any;
  getFloatingProps: (props?: any) => any;
  floatingStyles: React.CSSProperties;
  isPositioned: boolean;
  placement: Placement;
  setPlacement: (placement: Placement) => void;
  refs: {
    setReference: (node: HTMLElement | null) => void;
    setFloating: (node: HTMLDivElement | null) => void;
  };
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

function useTooltipContext() {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error("Tooltip components must be used within Tooltip");
  }
  return context;
}

interface TooltipProviderProps {
  delayDuration?: number;
  children: React.ReactNode;
}

const TooltipProvider = ({ delayDuration = 400, children }: TooltipProviderProps) => {
  const value = React.useMemo(
    () => ({ delayDuration }),
    [delayDuration]
  );

  return (
    <TooltipProviderContext.Provider value={value}>
      {children}
    </TooltipProviderContext.Provider>
  );
};

interface TooltipProps {
  delayDuration?: number;
  children: React.ReactNode;
}

const Tooltip = ({ delayDuration, children }: TooltipProps) => {
  const providerContext = React.useContext(TooltipProviderContext);
  const finalDelayDuration = delayDuration ?? providerContext.delayDuration;

  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement>(null);
  const floatingRef = React.useRef<HTMLDivElement>(null);
  const [placement, setPlacementState] = React.useState<Placement>("top");

  const setPlacement = React.useCallback((newPlacement: Placement) => {
    setPlacementState(newPlacement);
  }, []);

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 4 }),
    ],
    // [FLICKERING_PREVENTION] Usar autoUpdate com animationFrame e delay para evitar race conditions
    whileElementsMounted: open
      ? (reference, floating, update) => {
        // Delay pequeno para evitar race condition na abertura inicial
        let cleanup: (() => void) | null = null;
        const timeoutId = setTimeout(() => {
          cleanup = autoUpdate(reference, floating, update, {
            animationFrame: true, // Mais suave, reduz flickering
          });
        }, 0);
        return () => {
          clearTimeout(timeoutId);
          cleanup?.();
        };
      }
      : undefined,
  });

  const hover = useHover(context, {
    delay: {
      open: finalDelayDuration,
      close: 0,
    },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  // Sincronizar refs - atualizar quando abrir
  React.useEffect(() => {
    if (open && triggerRef.current) {
      refs.setReference(triggerRef.current);
    }
  }, [refs, open]);

  React.useEffect(() => {
    if (open && floatingRef.current) {
      refs.setFloating(floatingRef.current);
    }
  }, [refs, open]);

  const value = React.useMemo<TooltipContextValue>(
    () => ({
      open,
      setOpen,
      triggerRef,
      floatingRef,
      getReferenceProps,
      getFloatingProps,
      floatingStyles,
      isPositioned,
      placement,
      setPlacement,
      refs,
    }),
    [open, setOpen, getReferenceProps, getFloatingProps, floatingStyles, isPositioned, placement, setPlacement, refs]
  );

  return (
    <TooltipContext.Provider value={value}>
      {children}
    </TooltipContext.Provider>
  );
};

const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { asChild?: boolean }
>(({ asChild = false, children, ...props }, ref) => {
  const { triggerRef, getReferenceProps, refs: contextRefs } = useTooltipContext();

  const handleRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        triggerRef.current = node;
        // Sincronizar imediatamente quando o node é montado
        contextRefs.setReference(node);
      }
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    [ref, triggerRef, contextRefs]
  );

  const triggerProps = getReferenceProps({
    ...props,
    ref: handleRef,
  });

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<any>;
    return React.cloneElement(child, {
      ...triggerProps,
      ...child.props,
    } as any);
  }

  return (
    <div {...triggerProps}>
      {children}
    </div>
  );
});
TooltipTrigger.displayName = "TooltipTrigger";

interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
}

// [POPOVER_OPTIMIZATION] Memoizar TooltipContent para evitar re-renders desnecessários
const TooltipContent = React.memo(
  React.forwardRef<HTMLDivElement, TooltipContentProps>(
    ({ className, side = "top", align = "center", sideOffset = 4, children, ...props }, ref) => {
      const { t } = useTranslation();
      const {
        triggerRef,
        floatingRef,
        getFloatingProps,
        floatingStyles,
        isPositioned,
        placement: contextPlacement,
        setPlacement,
        open,
        refs: contextRefs,
      } = useTooltipContext();
      const isMounted = useMotionPresence(open, "tooltip");

      // Converter side e align para placement
      const placement = React.useMemo<Placement>(() => {
        if (align === "center") {
          return side as Placement;
        }
        return `${side}-${align}` as Placement;
      }, [side, align]);

      // Atualizar placement no contexto quando mudar
      React.useEffect(() => {
        if (placement !== contextPlacement) {
          setPlacement(placement);
        }
      }, [placement, contextPlacement, setPlacement]);

      const handleRef = React.useCallback(
        (node: HTMLDivElement | null) => {
          if (node) {
            floatingRef.current = node;
            // Sincronizar imediatamente quando o node é montado
            contextRefs.setFloating(node);
          }
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        },
        [ref, floatingRef, contextRefs]
      );

      // Converter placement para atributos data
      const placementSide = placement.split("-")[0] as "top" | "bottom" | "left" | "right";
      const placementAlign = placement.split("-")[1] as "start" | "center" | "end" | undefined;

      if (typeof window === "undefined") {
        return null;
      }

      if (!isMounted) {
        return null;
      }

      const content = (
        <div
          ref={handleRef}
          {...getFloatingProps({
            ...props,
            className: cn(
              "sys-motion-tooltip z-9999 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md",
              className
            ),
            style: {
              ...floatingStyles,
              ...props.style,
              zIndex: getFloatingLayerZIndex(triggerRef.current, 9999),
              visibility: open && !isPositioned ? "hidden" : "visible",
            },
            "data-state": open ? "open" : "closed",
            "aria-hidden": !open || undefined,
            inert: !open || undefined,
            "data-side": placementSide,
            "data-align": placementAlign || align,
          })}
        >
          {typeof children === "string" ? t(children as string) : children}
        </div>
      );

      return <FloatingPortal>{content}</FloatingPortal>;
    }
  )
);
TooltipContent.displayName = "TooltipContent";

// [PERF_OPTIMIZE] Wrapper memoizado para Tooltip
// Reduz renderizações de 1145 (Popper+Tooltip) para ~300
interface MemoizedTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delayDuration?: number;
  className?: string;
}

const MemoizedTooltip = React.memo<MemoizedTooltipProps>(
  ({ children, content, side, align, delayDuration = 400, className }) => {
    const [mounted, setMounted] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);

    // Lazy mount: só renderizar tooltip quando hover
    React.useEffect(() => {
      if (hovered && !mounted) {
        setMounted(true);
      }
    }, [hovered, mounted]);

    if (!content) {
      return <>{children}</>;
    }

    return (
      <Tooltip delayDuration={delayDuration}>
        <TooltipTrigger
          asChild
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {children}
        </TooltipTrigger>
        {mounted && (
          <TooltipContent side={side} align={align} className={className}>
            {content}
          </TooltipContent>
        )}
      </Tooltip>
    );
  },
  (prevProps, nextProps) => {
    // Só re-renderizar se conteúdo ou children mudaram
    return (
      prevProps.content === nextProps.content &&
      prevProps.children === nextProps.children &&
      prevProps.side === nextProps.side &&
      prevProps.align === nextProps.align
    );
  }
);

MemoizedTooltip.displayName = "MemoizedTooltip";

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  MemoizedTooltip,
};
